import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { Button } from "../components/ui/button";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import TaskDetailDialog from "../components/TaskDetailDialog";

const PRIO = { high: "bg-red-500/20 text-red-400 border-red-500/40", medium: "bg-amber-500/20 text-amber-400 border-amber-500/40", low: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" };
const pad = (n) => String(n).padStart(2, "0");

export default function Calendar() {
  const { t, lang } = useApp();
  const [tasks, setTasks] = useState([]);
  const [boards, setBoards] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/boards").then((r) => setBoards(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${y}-${pad(m + 1)}-${pad(d)}`);
  while (cells.length % 7) cells.push(null);
  const today = new Date().toISOString().slice(0, 10);
  const byDay = {};
  tasks.filter((x) => x.due_date).forEach((x) => { (byDay[x.due_date] = byDay[x.due_date] || []).push(x); });
  const monthTasks = tasks.filter((x) => x.due_date && x.due_date.startsWith(`${y}-${pad(m + 1)}`));
  const boardName = (id) => boards.find((b) => b.id === id)?.name;
  const dayNames = lang === "en" ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const monthLabel = cursor.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <CalendarDays className="h-7 w-7 text-primary" />{t("nav.calendar")}
        </h1>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today">{t("cal.today")}</Button>
          <Button size="icon" variant="outline" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <span className="ml-3 font-heading font-semibold capitalize" data-testid="cal-month-label">{monthLabel}</span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/40">
          {dayNames.map((d) => <div key={d} className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            const list = day ? byDay[day] || [] : [];
            const isToday = day === today;
            return (
              <div key={i} className={`min-h-[96px] border-b border-r border-border p-1.5 ${day ? "" : "bg-muted/20"} ${isToday ? "bg-primary/5" : ""}`} data-testid={day ? `cal-day-${day}` : undefined}>
                {day && (
                  <>
                    <div className={`text-xs font-mono mb-1 ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{Number(day.slice(-2))}</div>
                    <div className="space-y-1">
                      {list.slice(0, 3).map((x) => (
                        <button key={x.id} type="button" onClick={() => setDetail(x)} title={`${x.title}${x.board_id ? " · " + boardName(x.board_id) : ""}`}
                          className={`w-full text-left truncate rounded border px-1.5 py-0.5 text-[11px] leading-tight transition-colors hover:brightness-125 ${PRIO[x.priority] || PRIO.medium} ${x.column === "done" ? "opacity-50 line-through" : ""} ${x.due_date < today && x.column !== "done" ? "ring-1 ring-red-500/60" : ""}`}
                          data-testid={`cal-task-${x.id}`}>
                          {x.title}
                        </button>
                      ))}
                      {list.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{list.length - 3} {t("cal.more")}</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {monthTasks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="cal-empty">{t("cal.noDue")}</p>}
      {detail && <TaskDetailDialog task={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}
